// @flow
import * as React from 'react';
import { Fragment, useContext, useEffect, useState } from 'react';
import './preview.css'
import ProductQuantitySelect from "../productQuantitySelect/productQuantitySelect";
import AvailabilityStatus from "../availabilityStatus/availabilityStatus";
import classNames from "classnames";
import type { Product as ProductProps } from "../../types/product/types";
import QuickView from "../quickview/quickView";
import Product from "../product/product";
import useProductQuantity from "../../hooks/product/useProductQuantity";
import { useTranslation } from "react-i18next";
import { PRODUCT_COMPONENT } from "../../locales/components/namespaces";
import { AvailabilityStatus as ProductAvailabilityStatus } from "../../helpers/constants";
import HighlightedString from "../highlightedString/highlightedString";
import Util from "../util/util";
import { useOrderData } from "../../hooks/order/useOrderData";
import { MenuContext } from "../../pages/main/main";
import SelectOrderModal from "../selectOrderModal/selectOrderModal";
import SelectCreateOrderModal from "../selectCreateOrderModal/selectCreateOrderModal";
import { useUserData } from "../../hooks/user/useUserData";
import PreviewAdded from "./previewAdded";
import Dot from "../dot/dot";
import { getDiscountQuantity } from "../../helpers/helpers";
import PreviewThumbnail from "./previewThumbnail";
import useFavoriteProduct from "../../hooks/product/useFavoriteProduct";
import _ from "lodash";
import PreviewLabel from "./previewLabel";
import Button from "../button/button";
import BackgroundLoader from "../backgroundLoader/backgroundLoader";

type Props = {
	onAdd?: Function,
	title?: string,
	orderType?: number,
	code?: string,
	product?: ProductProps,
	disabled?: boolean,
	disabledFilled?: boolean,
	query?: string,
	displayAsTile?: boolean,
	displayMini?: boolean,
	orderId?: string,
	selectionMode?: string
}

const Preview = (props: Props) => {
	const { menuCategoryType: menuCategoryOrderType } = useContext(MenuContext)
	const { t } = useTranslation(PRODUCT_COMPONENT)
	const {
		orderId,
		product: {
			retailPrice,
			step,
			labels,
			discountWeb,
			thumbnail,
			title,
			invoicePolicy,
			message,
			code,
			unitOfMeasurement,
			multiplier,
			basicUnitOfMeasurement,
			availabilityStatus,
			exclusiveToOrderTypes
		},
		displayAsTile = false,
		displayMini = false,
		query,
		onAdd,
		disabled,
		disabledFilled,
		orderType,
		selectionMode = 'prefer-active-order-of-type'
	} = props
	const { favorite, toggleFavorite } = useFavoriteProduct(props.product)
	const [showProduct, setShowProduct] = useState(false)
	const [showRoundError, setShowRoundError] = useState(false)
	const [showCreatingError, setShowCreatingOrderError] = useState(false)
	const [showSelectOrderModal, setShowSelectOrderModal] = useState({
		visible: false,
		callback: null
	})
	const [showCreateOrderModal, setShowCreateOrderModal] = useState({
		visible: false,
		callback: null
	})
	const { activeOrder, creatingOrder, loading } = useOrderData()
	const productOrderTypes = exclusiveToOrderTypes || []
	const { data } = useUserData()
	const orderTypes = data && data.orderTypes ? data.orderTypes.filter(type => productOrderTypes.indexOf(type.id) > -1) : []
	const {
		quantity,
		setQuantity
	} = useProductQuantity(props.product, orderType ? orderType : menuCategoryOrderType, orderId, (qty) => onCommitQuantity(qty), undefined, disabled, selectionMode)
	
	const discountQuantity = getDiscountQuantity(props.product, quantity)
	
	const submitForOrderType = (quantity, selectedOrderType) => {
		const onSubmit = (order, nextOrderType) => {
			onAdd(props.product, quantity, order, nextOrderType)
		}
		const activeOrderOfType = activeOrder.find(o => o.type === selectedOrderType)
		return activeOrderOfType
			? onSubmit(activeOrderOfType)
			: onSubmit(null, selectedOrderType)
	}
	
	const openOrderTypeModal = (quantity) => {
		setShowCreateOrderModal({
			visible: true,
			callback: (selectedOrderType) => submitForOrderType(quantity, selectedOrderType)
		})
	}
	
	const onCommitQuantity = (quantity) => {
		if (availabilityStatus === ProductAvailabilityStatus.OutOfStock || !onAdd) return
		
		const onSubmit = (order, orderType) => {
			onAdd(props.product, quantity, order, orderType)
		}
		
		if (productOrderTypes.length === 0) return
		
		// For specific-order mode, always target that specific order
		if (selectionMode === 'specific-order') {
			const specificOrder = activeOrder.find(o => o.id === orderId)
			if (specificOrder) {
				onSubmit(specificOrder)
			}
			return
		}
		
		// For prefer-active-order-of-type mode: check for active order of preferred type FIRST
		if (selectionMode === 'prefer-active-order-of-type') {
			const preferredType = orderType ? orderType : menuCategoryOrderType
			
			if (preferredType && productOrderTypes.indexOf(preferredType) > -1) {
				const activeOrderOfPreferredType = activeOrder.find(o => o.type === preferredType)
				if (activeOrderOfPreferredType) {
					// Found active order of preferred type → submit directly (no popup)
					onSubmit(activeOrderOfPreferredType)
					return
				}
			}
			
			// No active order of preferred type: check for any other compatible active orders
			const compatibleActiveOrders = activeOrder.filter(o => productOrderTypes.indexOf(o.type) > -1)
			
			if (compatibleActiveOrders.length === 1) {
				// Exactly one other compatible order → submit to it
				onSubmit(compatibleActiveOrders[0])
				return
			} else if (compatibleActiveOrders.length > 1) {
				// Multiple other compatible orders → let user choose
				setShowSelectOrderModal({
					visible: true,
					callback: (order) => onSubmit(order)
				})
				return
			}
			
			// No active orders of any compatible type: fall through to type selection
			if (preferredType && productOrderTypes.indexOf(preferredType) > -1) {
				// Preferred type is applicable → create new order of preferred type silently
				submitForOrderType(quantity, preferredType)
				return
			}
		
			// Preferred type not applicable: fall back to accessible type count
			if (orderTypes.length === 1) {
				submitForOrderType(quantity, orderTypes[0].id)
			} else if (orderTypes.length > 1) {
				openOrderTypeModal(quantity)
			}
			return
		}
		
		// No active orders: fall through to type-selection logic (paths 3-4)
		// Use orderTypes (user-accessible types) not productOrderTypes (raw exclusiveToOrderTypes)
		// so products with inaccessible types don't trigger unnecessary popups
		if (selectionMode === 'popup-on-multiple') {
			// Check for compatible active orders first (paths 1-2)
			const compatibleActiveOrders = activeOrder.filter(o => productOrderTypes.indexOf(o.type) > -1)
			
			if (compatibleActiveOrders.length === 1) {
				// Path 1: Exactly one active order → submit directly
				onSubmit(compatibleActiveOrders[0])
				return
			} else if (compatibleActiveOrders.length > 1) {
				// Path 2: Multiple active orders → let user choose which one
				setShowSelectOrderModal({
					visible: true,
					callback: (order) => onSubmit(order)
				})
				return
			}
			
			// No active orders: use accessible type count (paths 3-4)
			if (orderTypes.length > 1) {
				// Multiple accessible types, no active order → choose type
				openOrderTypeModal(quantity)
				return
			}
			if (orderTypes.length === 1) {
				// Single accessible type, no active order → create silently
				submitForOrderType(quantity, orderTypes[0].id)
			}
			return
		}
	}
	
	const handleRemove = (order, orderType) => {
		onAdd(props.product, 0, order, orderType)
	}
	
	useEffect(() => {
		if (showRoundError) {
			setTimeout(() => {
				setShowRoundError(false)
			}, 4000)
		}
	}, [showRoundError])
	
	useEffect(() => {
		if (!creatingOrder) {
			setShowCreatingOrderError(false)
		}
	}, [creatingOrder])
	
	const onQuantityChange = (e) => {
		if (creatingOrder) {
			return setShowCreatingOrderError(true)
		}
		let newQuantity = e.target.valueAsNumber
		if (isNaN(newQuantity)) {
			newQuantity = 0
		}
		if (newQuantity && step) {
			const temp = newQuantity
			newQuantity = Math.floor(newQuantity / step) * step
			if (newQuantity !== temp) {
				setShowRoundError(true)
			}
			if (newQuantity === 0) {
				newQuantity = step
			}
		}
		setQuantity(newQuantity)
	}
	
	const label = labels && labels.length > 0 ?
		_.orderBy(labels, 'weight')[0] : null
	
	return (
		<div className={classNames('preview__wrapper', {
			'preview__wrapper--disabled': disabled,
			'preview__wrapper--disabledFilled': disabledFilled,
			'preview__wrapper--displayAsTile': displayAsTile,
			'preview__wrapper--displayMini': displayMini,
		})}>
			<PreviewThumbnail
				label={label}
				displayAsTile={displayAsTile}
				displayMini={displayMini}
				disabled={disabled}
				thumbnail={thumbnail}
				title={title}
				message={message}
				onClick={() => setShowProduct(true)}
			/>
			<div className={'preview__content'}>
				<div className={classNames('preview', {
					'preview--disabled': disabled,
					'preview--disabledFilled': disabledFilled,
					'preview--displayAsTile': displayAsTile,
					'preview--displayMini': displayMini
				})}>
					<div
						style={!onAdd ? { cursor: "initial" } : null}
					>
						<div>
							{!displayMini && !displayAsTile && label && (
								<PreviewLabel
									label={label}
									showColor={false}
								/>
							)}
							{!displayMini && (
								<Fragment>
									<strong
										className="preview__info"
										onClick={() => {
											if (onAdd) {
												setShowProduct(true);
											}
										}}>
										{(query && code) ? (
											<HighlightedString
												text={code}
												query={query}
											/>
										) : code}
									</strong>
									-
									<span>
                                        {(query && title) ? (
											<HighlightedString
												text={title}
		                                        query={query}
											/>
										) : title}
                                    </span>
								</Fragment>
							)}
							{displayMini && (
								<Fragment>
									<div
										className="preview__info"
										onClick={() => {
											if (onAdd) {
												setShowProduct(true);
											}
										}}>{title}</div>
									<Button variant={"link"}>{t('VIEW MORE')}</Button>
								</Fragment>
							)}
						</div>
						{!displayMini && (
							<Fragment>
								<div className={'preview__details'}>
									<span>{t('Package')}: 1 {unitOfMeasurement}</span>
									<span>|</span>
									<span>{t('Quantity')}: {multiplier} {basicUnitOfMeasurement}</span>
									<span>|</span>
									<span>{t('Retail Price')}: <strong>{parseInt(retailPrice) === 0 ? '-' : `${retailPrice}${data?.currencySymbol}/${t('Package')}`}</strong></span>
								</div>
								<Util justifyContent={"initial"} align={"center"}>
									<AvailabilityStatus status={availabilityStatus}/>
									{!disabled && (
										<div className={'preview__message show-mobile'}>
											<div dangerouslySetInnerHTML={{ __html: message }}/>
										</div>
									)}
								</Util>
							</Fragment>
						)}
					</div>
					{!displayMini && (
						<ProductQuantitySelect
							step={step}
							quantity={quantity}
							disabled={disabled}
							product={props.product}
							showRoundError={showRoundError}
							showCreatingError={showCreatingError}
							onQuantityChange={onQuantityChange}
						/>
					)}
					<QuickView
						show={showProduct}
						onClose={() => setShowProduct(false)}
					>
						<Product
							product={props.product}
							onClose={() => setShowProduct(false)}
							onSubmit={onCommitQuantity}
							onAdd={onAdd}
							orderType={orderType}
							orderId={orderId}
							selectionMode={selectionMode}
						/>
					</QuickView>
				</div>
				{!displayMini && (
					<Fragment>
						{Boolean(invoicePolicy) && (
							<div className={'preview preview--discount'} aria-label={'discount-policy-line'}>
								<div className="preview__info">
									<Dot type={4}/>
									<span
										style={{ marginLeft: '.5rem' }}><strong>{t('With Discount')}</strong> - {invoicePolicy.discountPercentage?.toFixed(2)}%</span>
								</div>
								<ProductQuantitySelect
									disabled
									useDiscount
									product={props.product}
									quantity={discountQuantity.toFixed(0)}
								/>
							</div>
						)}
						{Boolean(discountWeb) && (
							<div className={'preview preview--discount'}>
								<div className="preview__info">
									<Dot type={4}/>
									<span
										style={{ marginLeft: '.5rem' }}><strong>{t('With Discount')}</strong> - {discountWeb?.toFixed(2)}%</span>
								</div>
							</div>
						)}
						{!disabled && exclusiveToOrderTypes && exclusiveToOrderTypes.map(type => (
							<PreviewAdded
								key={type}
								type={type}
								product={props.product}
								onRemove={handleRemove}
							/>
						))}
					</Fragment>
				)}
			</div>
			<SelectOrderModal
				show={showSelectOrderModal.visible}
				onClose={() => setShowSelectOrderModal({visible: false, callback: null})}
				onSubmit={showSelectOrderModal.callback}
				types={productOrderTypes}
			/>
			<SelectCreateOrderModal
				show={showCreateOrderModal.visible}
				onClose={() => setShowCreateOrderModal({visible: false, callback: null})}
				onSubmit={showCreateOrderModal.callback}
				types={orderTypes}
			/>
			{!displayMini && (
				<button
					onClick={toggleFavorite}
					className={classNames('make-favorite', {
						'make-favorite--true': favorite
					})}>
					<span>{t('FAVORITE')}</span><span>❤</span>
				</button>
			)}
			{loading && <BackgroundLoader/>}
		</div>
	)
}

export default Preview